import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Alert, Spinner, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faBed,
  faBath,
  faRuler,
  faCalendarAlt,
  faCoins,
  faPhone,
  faEnvelope,
  faArrowLeft,
  faExternalLinkAlt,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

interface SharedPropertyData {
  mlsNumber: string;
  price: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  bedrooms: number;
  bathrooms: number;
  sqft?: number;
  yearBuilt?: number;
  propertyType: string;
  description?: string;
  features?: string[];
  images?: string[];
  listingAgent?: {
    name: string;
    phone?: string;
    email?: string;
    officeName?: string;
  };
  daysOnMarket?: number;
  pool?: boolean;
  garage?: number;
}

const SharedProperty: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<SharedPropertyData | null>(null);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    if (shareToken) {
      fetchSharedProperty();
    }
  }, [shareToken]);

  const fetchSharedProperty = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api/'}mls-portal/actions/shared/${shareToken}`
      );

      if (response.data.success) {
        setProperty(response.data.data);
        setMessage(response.data.message || '');
      } else {
        setError(response.data.message || 'Property not found');
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('This shared property link is invalid or has been removed.');
      } else if (err.response?.status === 410) {
        setError('This share link has expired.');
      } else {
        setError('Failed to load property. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading property details...</p>
        </div>
      </Container>
    );
  }

  if (error || !property) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Unable to Load Property</h4>
          <p>{error || 'Property not found'}</p>
          <Button variant="primary" onClick={() => navigate('/mls-portal')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Go to MLS Portal
          </Button>
        </Alert>
      </Container>
    );
  }

  const allImages = property.images?.length ? property.images : [];

  return (
    <Container className="py-4">
      {/* Back Button */}
      <Button 
        variant="outline-secondary" 
        size="sm" 
        className="mb-3"
        onClick={() => navigate('/mls-portal')}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
        Back to MLS Portal
      </Button>

      {/* Shared Message */}
      {message && (
        <Alert variant="info" className="mb-4">
          <strong>Message from sender:</strong> {message}
        </Alert>
      )}

      <Row>
        {/* Image Gallery */}
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body>
              {allImages.length > 0 ? (
                <Carousel 
                  activeIndex={activeImageIndex}
                  onSelect={setActiveImageIndex}
                  className="property-carousel"
                >
                  {allImages.map((image: string, index: number) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100"
                        src={image}
                        alt={`Property ${index + 1}`}
                        style={{ maxHeight: '500px', objectFit: 'cover' }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <div className="text-center py-5 bg-light">
                  <FontAwesomeIcon icon={faHome} size="4x" className="text-muted" />
                  <p className="mt-3 text-muted">No images available</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Property Description */}
          {property.description && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Property Description</h5>
                <p>{property.description}</p>
              </Card.Body>
            </Card>
          )}

          {/* Features */}
          {property.features && property.features.length > 0 && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Features & Amenities</h5>
                <Row>
                  {property.features.map((feature, index) => (
                    <Col md={6} key={index} className="mb-2">
                      <Badge bg="secondary" className="me-2">•</Badge>
                      {feature}
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* Property Details Sidebar */}
        <Col lg={4}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              {/* Price */}
              <h3 className="text-primary mb-3">{formatPrice(property.price)}</h3>
              
              {/* Address */}
              <p className="mb-3">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-muted" />
                {property.address.street}<br />
                <span className="ms-4">{property.address.city}, {property.address.state} {property.address.zip}</span>
              </p>

              <hr />

              {/* Key Details */}
              <div className="mb-3">
                <Row className="g-3">
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faBed} size="lg" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{property.bedrooms}</strong></p>
                      <small className="text-muted">Bedrooms</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faBath} size="lg" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{property.bathrooms}</strong></p>
                      <small className="text-muted">Bathrooms</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faRuler} size="lg" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{property.sqft?.toLocaleString() || 'N/A'}</strong></p>
                      <small className="text-muted">Sq Ft</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <FontAwesomeIcon icon={faCalendarAlt} size="lg" className="text-primary mb-2" />
                      <p className="mb-0"><strong>{property.yearBuilt || 'N/A'}</strong></p>
                      <small className="text-muted">Year Built</small>
                    </div>
                  </Col>
                </Row>
              </div>

              <hr />

              {/* Additional Info */}
              <div className="small">
                <p className="mb-2">
                  <strong>Property Type:</strong> {property.propertyType}
                </p>
                <p className="mb-2">
                  <strong>MLS #:</strong> {property.mlsNumber}
                </p>
                {property.daysOnMarket !== undefined && (
                  <p className="mb-2">
                    <strong>Days on Market:</strong> {property.daysOnMarket}
                  </p>
                )}
                {property.pool && (
                  <Badge bg="info" className="me-2">Pool</Badge>
                )}
                {property.garage && property.garage >= 3 && (
                  <Badge bg="secondary">{property.garage} Car Garage</Badge>
                )}
              </div>

              {/* Listing Agent */}
              {property.listingAgent && (
                <>
                  <hr />
                  <h6>Listing Information</h6>
                  <p className="mb-2">
                    <strong>{property.listingAgent.name}</strong>
                  </p>
                  {property.listingAgent.officeName && (
                    <p className="mb-2 small text-muted">{property.listingAgent.officeName}</p>
                  )}
                  {property.listingAgent.phone && (
                    <p className="mb-2">
                      <FontAwesomeIcon icon={faPhone} className="me-2" />
                      <a href={`tel:${property.listingAgent.phone}`}>{property.listingAgent.phone}</a>
                    </p>
                  )}
                  {property.listingAgent.email && (
                    <p className="mb-2">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      <a href={`mailto:${property.listingAgent.email}`}>{property.listingAgent.email}</a>
                    </p>
                  )}
                </>
              )}

              {/* CTA Button */}
              <div className="d-grid mt-4">
                <Button variant="primary" size="lg" onClick={() => navigate('/mls-portal')}>
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                  View More Properties
                </Button>
              </div>
            </Card.Body>
          </Card>
          {/* ARMLS IDX 23.3.3 + 23.3.4 — source + VERBATIM disclaimer (July 2026 audit cure) */}
          <div className="text-muted mt-3" style={{ fontSize: '11px', lineHeight: 1.5 }}>
            <p className="mb-1">Listing data provided by Arizona Regional Multiple Listing Service (ARMLS). Each listing is courtesy of its respective listing brokerage.</p>
            <p className="mb-1">All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.</p>
            <p className="mb-0">Equal Housing Opportunity. &copy; {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc.</p>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default SharedProperty;